import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiChevronRight, FiPackage, FiPlus, FiRefreshCw, FiSearch } from 'react-icons/fi';
import {
  createStockRequest,
  getIncomingPendingStockRequests,
  getStockRequests,
} from '../api';
import { normalizePaginatedResponse } from '../utils/pagination';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import InlineLoading from '../components/InlineLoading';

const STATUS_META = {
  draft: { label: 'Borrador', tone: 'bg-gray-100 text-gray-700 border-gray-200' },
  sent: { label: 'Enviada', tone: 'bg-amber-100 text-amber-700 border-amber-200' },
  fulfilled: { label: 'Completada', tone: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const PLACEHOLDER_IMAGE = 'https://placehold.co/96';

const formatQuantity = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0';
  const hasDecimals = Math.abs(amount - Math.round(amount)) > 0.0001;
  return amount.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 2 }
    : undefined);
};

const resolveItemProductName = (item) =>
  item?.Variety?.Product?.productName || item?.productName || '';

const resolveItemVarietyName = (item) =>
  item?.varietyName || item?.Variety?.varietyName || 'Producto';

const resolveItemImage = (item) =>
  item?.varietyImage ||
  item?.Variety?.varietyImage ||
  item?.Variety?.Product?.productImage ||
  PLACEHOLDER_IMAGE;

const buildItemDisplayName = (item) => {
  const productName = String(resolveItemProductName(item) || '').trim();
  const varietyName = String(resolveItemVarietyName(item) || '').trim();
  if (productName && varietyName && productName.toLowerCase() !== varietyName.toLowerCase()) {
    return `${productName} - ${varietyName}`;
  }
  return productName || varietyName || 'Producto';
};

const listMatchesSearch = (list, search) => {
  if (!search) return true;
  const haystack = [
    list?.name,
    list?.RequestBranch?.branchName,
    list?.TargetBranch?.branchName,
    ...(list?.Items || []).flatMap((item) => [
      resolveItemProductName(item),
      resolveItemVarietyName(item),
      item?.metricUnit,
    ]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(search);
};

const StockRequestsListPage = () => {
  const navigate = useNavigate();
  const { branchId: routeBranchId } = useParams();
  const branchId = routeBranchId || localStorage.getItem('selectedBranchId');
  const { user } = useCurrentUser();

  const canReadStock = hasPermission(user, 'stock.read');
  const canCreateStock = hasPermission(user, 'stock.create');

  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [isMainBranch, setIsMainBranch] = useState(false);

  const fetchLists = useCallback(async () => {
    if (!branchId || !canReadStock) return;
    setLoading(true);
    setError('');
    try {
      const params = { pageSize: 0 };
      if (statusFilter) params.status = statusFilter;
      const data = isMainBranch
        ? await getIncomingPendingStockRequests(branchId, params)
        : await getStockRequests(branchId, params);
      const normalized = normalizePaginatedResponse(data);
      setLists(normalized.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'No se pudieron cargar las solicitudes.');
    } finally {
      setLoading(false);
    }
  }, [branchId, canReadStock, statusFilter, isMainBranch]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists, refreshKey]);

  useEffect(() => {
    if (!branchId) return;
    let mounted = true;
    const detectMainBranch = async () => {
      try {
        await getIncomingPendingStockRequests(branchId, { pageSize: 1 });
        if (mounted) setIsMainBranch(true);
      } catch (err) {
        if (mounted) setIsMainBranch(false);
      }
    };
    detectMainBranch();
    return () => {
      mounted = false;
    };
  }, [branchId]);

  const handleCreate = async () => {
    if (!canCreateStock || isMainBranch || !branchId) return;
    setCreating(true);
    setCreateError('');
    try {
      const response = await createStockRequest(branchId, {});
      const nextId = response?.requestList?.id;
      if (nextId) {
        navigate(`/branches/${branchId}/stock-requests/${nextId}?add=1`);
        return;
      }
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      setCreateError(err?.response?.data?.message || 'No se pudo crear la solicitud.');
    } finally {
      setCreating(false);
    }
  };

  const renderedLists = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return (lists || []).filter((list) => listMatchesSearch(list, search));
  }, [lists, searchTerm]);

  const pageTitle = isMainBranch ? 'Solicitudes entrantes' : 'Solicitudes a matriz';
  const pageDescription = isMainBranch
    ? 'Revisa los productos pedidos por las sucursales y prepara guías de traslado.'
    : 'Gestiona listas de productos solicitados a la sucursal principal.';

  const renderRequestCard = (list) => {
    const meta = STATUS_META[list.status] || STATUS_META.draft;
    const items = Array.isArray(list.Items) ? list.Items : [];
    const previewItems = items.slice(0, 4);
    const hiddenItems = Math.max(0, items.length - previewItems.length);
    const completionRatio = Math.max(0, Math.min(1, Number(list.progress?.completionRatio || 0)));
    const requestBranchLabel = list.RequestBranch?.branchName || 'Sucursal';
    const targetBranchLabel = list.TargetBranch?.branchName || 'Matriz';

    return (
      <article
        key={list.id}
        className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:border-blue-200 hover:shadow-md"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-lg font-semibold text-gray-900">
                {list.name || `Solicitud #${list.id}`}
              </h2>
              <span className="text-xs font-medium text-gray-400">#{list.id}</span>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {isMainBranch ? requestBranchLabel : `Destino: ${targetBranchLabel}`}
            </p>
          </div>
          <span className={`inline-flex shrink-0 rounded-full border px-2 py-1 text-xs ${meta.tone}`}>
            {meta.label}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
          <div className="rounded bg-gray-50 p-2">
            <p className="text-[11px] uppercase text-gray-500">Items</p>
            <p className="font-semibold text-gray-900">{items.length}</p>
          </div>
          <div className="rounded bg-gray-50 p-2">
            <p className="text-[11px] uppercase text-gray-500">Solicitado</p>
            <p className="font-semibold text-gray-900">{formatQuantity(list.progress?.requestedTotal)}</p>
          </div>
          <div className="rounded bg-gray-50 p-2">
            <p className="text-[11px] uppercase text-gray-500">Pendiente</p>
            <p className="font-semibold text-gray-900">{formatQuantity(list.progress?.remainingTotal)}</p>
          </div>
        </div>

        <div className="mt-3">
          <div className="h-2 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-emerald-500"
              style={{ width: `${completionRatio * 100}%` }}
            />
          </div>
          <div className="mt-1 flex justify-between text-xs text-gray-500">
            <span>Completado {Math.round(completionRatio * 100)}%</span>
            <span>Enviada {formatDateTime(list.sentAt)}</span>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {previewItems.length ? (
            previewItems.map((item) => (
              <div key={item.id} className="flex items-center gap-3 rounded border border-gray-100 bg-gray-50 p-2">
                <img
                  src={resolveItemImage(item)}
                  alt={buildItemDisplayName(item)}
                  className="h-12 w-12 shrink-0 rounded object-cover"
                  loading="lazy"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-gray-900">{buildItemDisplayName(item)}</p>
                  <p className="text-xs text-gray-500">
                    {formatQuantity(item.requestedQuantity)} {item.metricUnit || 'unidad'}
                    {Number(item.remainingQuantity || 0) > 0
                      ? ` · pendiente ${formatQuantity(item.remainingQuantity)}`
                      : ''}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="flex items-center gap-2 rounded border border-dashed border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
              <FiPackage />
              Sin productos agregados.
            </div>
          )}
          {hiddenItems > 0 && (
            <p className="text-xs font-medium text-gray-500">+{hiddenItems} productos más</p>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-gray-500">Creada {formatDateTime(list.createdAt)}</p>
          <button
            type="button"
            onClick={() => navigate(`/branches/${branchId}/stock-requests/${list.id}`)}
            className="inline-flex w-full items-center justify-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 sm:w-auto"
          >
            Ver detalle
            <FiChevronRight />
          </button>
        </div>
      </article>
    );
  };

  if (!canReadStock) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border p-4 text-sm text-gray-600">
          No tienes permisos para ver solicitudes.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
            <p className="text-sm text-gray-600">{pageDescription}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isMainBranch && canCreateStock && (
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating}
                className="inline-flex items-center justify-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                <FiPlus />
                {creating ? 'Creando...' : 'Nueva solicitud'}
              </button>
            )}
            <button
              type="button"
              onClick={() => setRefreshKey((prev) => prev + 1)}
              className="inline-flex items-center justify-center gap-2 rounded border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <FiRefreshCw />
              Refrescar
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_220px] md:items-end">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Buscar</span>
              <span className="relative block">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Sucursal, producto o solicitud"
                  className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </span>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Estado</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                <option value="">Todos</option>
                <option value="draft">Borrador</option>
                <option value="sent">Enviada</option>
                <option value="fulfilled">Completada</option>
              </select>
            </label>
          </div>

          {createError && <p className="mt-3 text-sm text-red-600">{createError}</p>}
          {isMainBranch && (
            <p className="mt-3 text-xs text-gray-500">
              Estás en la sucursal principal: la creación de solicitudes está deshabilitada aquí.
            </p>
          )}
        </div>

        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <InlineLoading label="Cargando solicitudes" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        ) : renderedLists.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-600 shadow-sm">
            No hay solicitudes para mostrar.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {renderedLists.map(renderRequestCard)}
          </div>
        )}
      </div>
    </div>
  );
};

export default StockRequestsListPage;
